/** New activity-category dialog: pick a tenant + title (+ optional icon), POST
 *  (JSON, or multipart when an icon is staged), then navigate to the created
 *  category. */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as categoriesApi from '@/api/activityCategories';
import { ConsoleApiError } from '@/api/client';
import { ImageUploadField, type ImageFieldValue } from '@/components/ImageUploadField/ImageUploadField';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { catalogWriteErrors } from '@/api/catalogWriteErrors';

export function ActivityCategoryCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState<ImageFieldValue>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setTitle('');
    setIcon(null);
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      categoriesApi.create(
        { tour_operator: tenant!.id, title: title.trim() },
        icon instanceof File ? icon : undefined,
      ),
    onSuccess: (category) => {
      snackbar.show('Category created.', 'success');
      qc.invalidateQueries({ queryKey: ['activity_categories'] });
      reset();
      onClose();
      navigate(`/activity-categories/${category.id}`);
    },
    onError: (e) => {
      // The title the server refused, else the request-level `detail` (the
      // message) — never `detail` / `code` read as fields.
      const titleErr = catalogWriteErrors(e)?.fields.title;
      setError(titleErr ?? (e instanceof ConsoleApiError ? e.message : 'Create failed.'));
    },
  });

  const canSubmit = tenant !== null && title.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New category</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TenantPicker
            value={tenant} featured={pageTenants} onChange={setTenant}
            label="Operator" placeholder="Select operator"
          />
          <TextField
            label="Title" size="small" fullWidth value={title} required
            onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 1024 }}
          />
          <ImageUploadField
            label="Category icon (optional)" currentUrl={null}
            value={icon} onChange={setIcon} canManage
          />
          {error && <Box sx={{ color: 'error.main', fontSize: 13 }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Creating…' : 'Create category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
